import { useEffect, useRef, useState } from 'react'
import { Badge } from 'react-bootstrap'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import Utils from '../utility/utility'
import { postApiCall } from '../service/PostApiCall'
import {
  NOTIFICATION,
  NOTIFICATION_READ,
  USER_NOTIFICATION,
  USER_NOTIFICATION_READ,
} from '../service/apiUrl'
import {
  getNotificationPermission,
  requestNotificationPermission,
  showBrowserNotification,
  unblockInstructions,
  watchNotificationPermission,
} from '../utility/browserNotification'
import NotificationComponent, {
  isUnread,
  notificationTitle,
} from './NotificationComponent'
import NotificationStack from './NotificationStack'
import {
  NOTIFICATIONS_REFRESH_EVENT,
  publishNotifications,
} from '../utility/notificationsFeed'
import SidebarMenu from './SidebarMenu'
import { Skeleton } from './Skeleton'

/* ============================================================================
   Notifications in the sidebar footer, for every role.

     admin (role 1)  /api/notifications       shared inbox: leave applications,
                                              support tickets (+ anything
                                              addressed to this admin)
     everyone else   /user-api/notifications  their own leave status changes,
                                              projects they were added to

   - polls every 10s while signed in; the interval is cleared on unmount
   - native browser notifications: anything unread with an id above the
     per-user high-water mark in localStorage (`notifSeen_<uid>`). The first
     poll on a browser only sets the mark, so signing in never replays
     history; a backlog of more than MAX_ANNOUNCED becomes one summary.
     Polling, not a time window, decides what is new, so a background tab
     whose timers the browser has throttled still announces everything. The
     mark advances even while alerts are off, so switching them on later does
     not replay what was missed.
   - desktop alerts are an on/off switch in the panel (`desktopAlerts` in
     localStorage, unset = on once the browser has granted permission).
     Switching on is the only place permission is requested — it needs a
     click, so never from the poll.
   - hovering (or keyboard-focusing) the Notifications row fans the unread
     items out of it as a NotificationStack; a card opens that item, the row
     itself still opens the full panel
   ========================================================================== */
const POLL_MS = 10 * 1000
const MAX_ANNOUNCED = 3
const ALERTS_KEY = 'desktopAlerts'

const seenKey = (uid) => `notifSeen_${uid}`

const alertsEnabled = () =>
  localStorage.getItem(ALERTS_KEY) !== 'off' &&
  getNotificationPermission() === 'granted'

const browserTitle = (item) => {
  if (item.recipient_id) return item.title
  return item.type === 'support' ? 'New support ticket' : 'New leave request'
}

const notificationTarget = (item, isAdmin) => {
  if (item.type === 'support') return `/add-support/${item.doc_id}`
  if (item.type === 'project') return `/my-projects/${item.doc_id}`
  if (!isAdmin) return '/my-leave'
  // inbox rows: user = the applicant; addressed rows: the leave is the recipient's
  return `/edit-leave/${item.doc_id}?uid=${item.recipient_id || item.user?.id}`
}

// past a week a day count stops reading as a time ("263d"), so show the date
const shortAgo = (unix) => {
  const at = moment.unix(unix)
  const seconds = moment().diff(at, 'seconds')
  if (seconds < 60) return 'now'
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`
  if (seconds < 7 * 86400) return `${Math.floor(seconds / 86400)}d`
  return at.format(at.isSame(moment(), 'year') ? 'D MMM' : 'D MMM YYYY')
}

const SidebarNotifications = ({ collapsed }) => {
  const [isActive, setIsActive] = useState(2)
  const [notificationData, setNotificationData] = useState([])
  /* The panel says "No notifications" when the list is empty, which is the
     wrong answer before the first poll has come back. Flipped on both settle
     paths of that poll. */
  const [loaded, setLoaded] = useState(false)
  const [panelOpen, setPanelOpen] = useState(false)
  const [stackOpen, setStackOpen] = useState(false)
  const [permission, setPermission] = useState(getNotificationPermission)
  const [alertsOn, setAlertsOn] = useState(alertsEnabled)

  const signdata = useSelector((state) => state.signinReducers)
  const token = signdata?.data?.access_token
  const uid = signdata?.data?.id
  const isAdmin = signdata?.data?.role == 1
  const listUrl = isAdmin ? NOTIFICATION : USER_NOTIFICATION
  const readUrl = isAdmin ? NOTIFICATION_READ : USER_NOTIFICATION_READ

  // a browser notification can be clicked long after the poll that raised it
  const navigate = useNavigate()
  const navigateRef = useRef(navigate)
  navigateRef.current = navigate

  const unread = notificationData.filter(isUnread)

  const openPanel = (item) => {
    if (isAdmin && item) setIsActive(item.type === 'support' ? 2 : 3)
    setStackOpen(false)
    setPanelOpen(true)
  }

  const openNotification = (item) => {
    setPanelOpen(false)
    setStackOpen(false)
    if (isUnread(item)) handleMarkAsRead(item.id).catch(() => {})
    navigateRef.current(notificationTarget(item, isAdmin))
  }

  const announce = (list) => {
    const key = seenKey(uid)
    const stored = localStorage.getItem(key)
    const newest = list.reduce((max, item) => Math.max(max, Number(item.id) || 0), 0)

    if (stored !== null && alertsEnabled()) {
      const seen = Number(stored) || 0
      const fresh = list.filter((item) => Number(item.id) > seen && isUnread(item))

      if (fresh.length > MAX_ANNOUNCED) {
        showBrowserNotification({
          title: `${fresh.length} new notifications`,
          body: fresh.slice(0, MAX_ANNOUNCED).map(notificationTitle).join(', ') + '…',
          tag: 'ems-notification-summary',
          onClick: () => openPanel(fresh[0]),
        })
      } else {
        // oldest first, so the newest lands on top of the OS stack
        fresh
          .slice()
          .reverse()
          .forEach((item) =>
            showBrowserNotification({
              title: browserTitle(item),
              body: item.description,
              tag: `ems-notification-${item.id}`,
              onClick: () => openNotification(item),
            }),
          )
      }
    }

    if (stored === null || newest > Number(stored)) {
      localStorage.setItem(key, String(newest))
    }
  }

  const handleNotification = async () => {
    try {
      const response = await postApiCall({}, token, listUrl)
      const list = (Array.isArray(response) ? response : [])
        .slice()
        .sort((a, b) => b.created_at - a.created_at)
      setNotificationData(list)
      publishNotifications(list)
      setPermission(getNotificationPermission())
      setAlertsOn(alertsEnabled())
      announce(list)
    } catch (error) {
      console.error('NOTIFICATION', error)
    } finally {
      setLoaded(true)
    }
  }

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!token) return undefined
    handleNotification()
    const interval = setInterval(handleNotification, POLL_MS)
    // the banner marked something read: catch the bell up now, not in 10s
    window.addEventListener(NOTIFICATIONS_REFRESH_EVENT, handleNotification)
    return () => {
      clearInterval(interval)
      window.removeEventListener(NOTIFICATIONS_REFRESH_EVENT, handleNotification)
    }
  }, [token])

  // the user unblocking (or blocking) the site in browser settings shows up
  // here immediately instead of on the next poll
  useEffect(
    () =>
      watchNotificationPermission((next) => {
        setPermission(next)
        setAlertsOn(alertsEnabled())
      }),
    [],
  )

  const handleMarkAsRead = async (values) => {
    try {
      await postApiCall({ id: values }, token, readUrl)
      handleNotification()
    } catch (error) {
      Utils.toast('error', error?.data?.message)
      throw error
    }
  }

  const toggleAlerts = async () => {
    if (alertsOn) {
      localStorage.setItem(ALERTS_KEY, 'off')
      setAlertsOn(false)
      return
    }
    // when blocked this resolves 'denied' without a prompt — the browser will
    // not ask again — but it is the right call the moment the user has reset
    // the site to "Ask" in their settings
    const result =
      permission === 'granted' ? 'granted' : await requestNotificationPermission()
    setPermission(result)
    if (result === 'granted') localStorage.setItem(ALERTS_KEY, 'on')
    setAlertsOn(alertsEnabled())
  }
  const alertsUnsupported = permission === 'unsupported'

  // only a mouse hovers; a tap would open the stack and the panel at once
  const anchorHandlers = {
    onPointerEnter: (e) => e.pointerType === 'mouse' && setStackOpen(true),
    onPointerLeave: (e) => e.pointerType === 'mouse' && setStackOpen(false),
    onFocus: (e) => e.target.matches(':focus-visible') && setStackOpen(true),
    onBlur: (e) => {
      if (!e.currentTarget.contains(e.relatedTarget)) setStackOpen(false)
    },
    onKeyDown: (e) => e.key === 'Escape' && setStackOpen(false),
  }

  const tab = (value, label, type) => (
    <button
      type="button"
      role="tab"
      aria-selected={isActive === value}
      className={'notif-tab' + (isActive === value ? ' is-active' : '')}
      onClick={() => setIsActive(value)}
    >
      <span className="notif-tab-label">{label}</span>
      {unread.some((item) => item.type === type) ? (
        <span className="notif-tab-dot" aria-hidden="true" />
      ) : null}
    </button>
  )

  return (
    <div className="sb-notif-anchor" {...(collapsed ? {} : anchorHandlers)}>
      <SidebarMenu
        collapsed={collapsed}
        label="Notifications"
        railWidth={320}
        railLabel={<span className="sb-bell-label">Notifications</span>}
        triggerClassName="sb-bell"
        open={panelOpen}
        onOpenChange={setPanelOpen}
        trigger={
          <>
            <span className="sb-bell-icon">
              <i className="fa fa-bell-o" aria-hidden="true"></i>
              {unread.length > 0 && (
                <Badge bg="danger" text="white" className="sb-bell-badge">
                  {unread.length}
                </Badge>
              )}
            </span>
            {collapsed ? null : (
              <span className="sb-bell-label">Notifications</span>
            )}
          </>
        }
      >
        <div className="sb-notif">
          <div className="notif-head">
            <span className="notif-head-title">Notifications</span>
            {unread.length > 0 ? (
              <span className="notif-head-count">{unread.length} unread</span>
            ) : null}
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={alertsOn}
            disabled={alertsUnsupported}
            className="notif-alerts"
            onClick={toggleAlerts}
          >
            <span>Desktop alerts</span>
            <span className="notif-switch" aria-hidden="true">
              <span className="notif-switch-knob" />
            </span>
          </button>
          {permission === 'denied' ? (
            <div className="notif-hint">
              <strong>Blocked by your browser.</strong> It will not ask again;
              only you can allow it. {unblockInstructions()}
            </div>
          ) : alertsUnsupported ? (
            <div className="notif-hint">
              This browser does not support desktop notifications.
            </div>
          ) : null}

          {isAdmin ? (
            <div className="notif-tabs" role="tablist">
              {tab(2, 'Support', 'support')}
              {tab(3, 'Leaves', 'leave')}
            </div>
          ) : null}

          <div className="notif-scroll">
            {loaded ? (
              <NotificationComponent
                notificationData={notificationData}
                isAdmin={isAdmin}
                isActive={isActive}
                markAsReadHandler={handleMarkAsRead}
                onOpen={openNotification}
              />
            ) : (
              <div className="notif-loading" role="status" aria-label="Loading notifications">
                {Array.from({ length: 4 }, (_, index) => (
                  <div key={index} className="leaveBox" aria-hidden="true">
                    <Skeleton width="45%" height={12} />
                    <Skeleton width="85%" height={11} style={{ marginTop: '.5rem' }} />
                    <Skeleton width="30%" height={10} style={{ marginTop: '.5rem' }} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </SidebarMenu>

      {/* after the row in the DOM, so Tab goes row -> cards */}
      {!collapsed ? (
        <NotificationStack
          open={stackOpen && !panelOpen}
          items={unread.map((item) => ({
            id: item.id,
            title: notificationTitle(item),
            description: item.description,
            trailing: shortAgo(item.created_at),
          }))}
          onSelect={(id) => {
            const item = unread.find((n) => n.id === id)
            if (item) openNotification(item)
          }}
        />
      ) : null}
    </div>
  )
}

export default SidebarNotifications
